// src/components/NoticeContent.tsx
import React from 'react';

type Props = {
  service: string;              // 例: 'aga_male' / 'pill_all' / 'pill_low' / 'pill_shift' / 'pill_after'
  lang: 'ja' | 'zh' | string;   // 'zh-CN' なども許容（内部で正規化）
  version?: string;             // 任意: キャッシュバスター。例: 'v2025-10-01'
};

type NoticeDocRaw = Record<string, unknown>;
type NoticeDoc = { title?: string; html?: string };

/** public ベースパス（CRA / Vite / 直配信 いずれでも動作） */
function getPublicBase(): string {
  // @ts-ignore
  // eslint-disable-next-line
  const viteBase = process.env.PUBLIC_URL || '';
  // @ts-ignore
  const craBase = (typeof process !== 'undefined' && process?.env?.PUBLIC_URL) || '';
  const base = viteBase || craBase || '/';
  return base.endsWith('/') ? base.slice(0, -1) : base;
}

/** 言語コードの正規化：zh 系はすべて zh、その他は ja */
function normalizeLang(input: string): 'zh' | 'ja' {
  const v = (input || '').toLowerCase();
  return v.startsWith('zh') ? 'zh' : 'ja';
}

/** service のエイリアス集合（上から順に優先して探す） */
function aliasesForService(svc: string): string[] {
  const key = (svc || '').toLowerCase().trim();

  // 旧来 'pill' 指定を総覧扱いに
  if (key === 'pill') return ['pill_all', 'pill', 'pill-overview', 'pill_overview'];

  // Pill (3タイプ) の優先候補を網羅
  if (key === 'pill_all' || key === 'pill-overview' || key === 'pill_overview') {
    return ['pill_all', 'pill', 'pill-overview', 'pill_overview'];
  }
  if (key === 'pill_low' || key === 'pill-low') {
    return [
      'pill_low', 'pill-low', 'pill_low-dose', 'pill_low_dose', 'pill_lowdose',
      'low-dose', 'low_dose', 'lowdose'
    ];
  }
  if (key === 'pill_shift' || key === 'pill-shift' || key === 'period-shift' || key === 'period_shift') {
    return [
      'pill_shift', 'pill-shift', 'pill_period-shift', 'pill_period_shift',
      'period-shift', 'period_shift'
    ];
  }
  if (key === 'pill_after' || key === 'pill-after' || key === 'after-pill' || key === 'after_pill') {
    return [
      'pill_after', 'pill-after', 'after-pill', 'after_pill'
    ];
  }

  // それ以外は素のキーを返す
  return [key];
}

/** スネーク/ケバブの相互変換を含めたバリアント生成 */
function expandVariants(name: string): string[] {
  const out = new Set<string>();
  out.add(name);
  if (name.includes('_')) out.add(name.replace(/_/g, '-'));
  if (name.includes('-')) out.add(name.replace(/-/g, '_'));
  return Array.from(out);
}

/** JSON の緩やかな正規化：html / content / body / markdown を取り込む */
function normalizeDoc(raw: NoticeDocRaw | null): NoticeDoc | null {
  if (!raw || typeof raw !== 'object') return null;

  const obj = raw as Record<string, unknown>;
  const title =
    (typeof obj.title === 'string' && obj.title.trim()) ||
    (typeof obj.name === 'string' && obj.name.trim()) ||
    '';

  let html =
    (typeof obj.html === 'string' && obj.html) ||
    (typeof obj.content === 'string' && obj.content) ||
    (typeof obj.body === 'string' && obj.body) ||
    (typeof obj.markdown === 'string' && obj.markdown) ||
    '';

  // プレーンテキストのみのときは <p> で包んで HTML 化
  if (html && !/[<][a-zA-Z!/]/.test(html)) {
    html = `<p>${escapeHtml(html)}</p>`;
  }

  if (!title && !html) return null;
  return { title, html };
}

/** 最低限のエスケープ */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** モジュールスコープのメモリキャッシュ：アンマウント→再マウントでも維持 */
const memoryCache: Record<string, NoticeDoc> = {};

const NoticeContent: React.FC<Props> = ({ service, lang, version }) => {
  const base = getPublicBase();
  const normLang = normalizeLang(lang as string);
  const qs = version ? `?v=${encodeURIComponent(version)}` : '';

  // ✅ 1) サービス別のエイリアス列（pill_* を賢く解決）
  const aliasList = React.useMemo(() => aliasesForService(service), [service]);

  // ✅ 2) 各エイリアスに対し _ / - の相互変換を広げる
  const svcList = React.useMemo(() => {
    const acc = new Set<string>();
    for (const a of aliasList) for (const v of expandVariants(a)) acc.add(v);
    return Array.from(acc);
  }, [aliasList]);

  // ✅ 3) 取得候補 URL（言語付き→反対言語→言語なし）
  const urlCandidates: string[] = React.useMemo(() => {
    const arr: string[] = [];
    for (const svc of svcList) arr.push(`${base}/data/notices/${svc}.${normLang}.json${qs}`);
    for (const svc of svcList) arr.push(`${base}/data/notices/${svc}.${normLang === 'zh' ? 'ja' : 'zh'}.json${qs}`);
    for (const svc of svcList) arr.push(`${base}/data/notices/${svc}.json${qs}`);
    return arr;
  }, [svcList, base, normLang, qs]);

  const cacheKey = React.useMemo(
    () => `${svcList.join('|')}:${normLang}:${version || ''}`,
    [svcList, normLang, version]
  );

  /** 重要：初期 state をキャッシュから同期取得（折りたたみ→再展開で“準備中”を回避） */
  const cached = memoryCache[cacheKey] || null;
  const [doc, setDoc] = React.useState<NoticeDoc | null>(cached);
  const [loading, setLoading] = React.useState<boolean>(!cached);
  const [error, setError] = React.useState<string | null>(null);

  /** “最後に良かった文面” を保持：再取得失敗時でも表示を崩さない */
  const lastGoodDocRef = React.useRef<NoticeDoc | null>(cached);

  React.useEffect(() => {
    let mounted = true;

    async function fetchJson(url: string): Promise<NoticeDoc | null> {
      try {
        const res = await fetch(url, { cache: 'no-cache' });
        if (!res.ok) return null;
        const raw = (await res.json()) as NoticeDocRaw;
        const normalized = normalizeDoc(raw);
        if (normalized) {
          console.debug('[NoticeContent] loaded:', { url, keys: Object.keys(raw) });
        } else {
          console.debug('[NoticeContent] json parsed but empty normalized result:', { url, raw });
        }
        return normalized;
      } catch (e) {
        console.warn('[NoticeContent] fetch failed:', url, e);
        return null;
      }
    }

    async function run() {
      // service が空のときは即終了
      if (!service) {
        setLoading(false);
        return;
      }

      // すでにキャッシュがあれば即表示して終了（不要な点滅を防止）
      if (memoryCache[cacheKey]) {
        setDoc(memoryCache[cacheKey]);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      let found: NoticeDoc | null = null;
      for (const url of urlCandidates) {
        const result = await fetchJson(url);
        if (result) {
          found = result;
          break;
        }
      }

      if (!mounted) return;

      if (found) {
        memoryCache[cacheKey] = found;
        lastGoodDocRef.current = found;
        setDoc(found);
        setError(null);
      } else {
        // 取得失敗時：直近の良い文面があればそれを出し続ける
        if (lastGoodDocRef.current) {
          setDoc(lastGoodDocRef.current);
          setError(null);
        } else {
          setDoc(null);
          setError('no_valid_notice_json');
        }
      }
      setLoading(false);
    }

    run();
    return () => { mounted = false; };
  }, [service, cacheKey, urlCandidates]);

  if (loading) {
    return (
      <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">
        {normLang === 'ja' ? '読み込み中…' : '加载中…'}
      </div>
    );
  }

  // 「準備中」を出すのは “一度も成功したことがない” ときだけ
  if ((!doc || (!doc.title && !doc.html)) && error) {
    return (
      <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">
        {normLang === 'ja' ? 'この項目の注意事項は現在準備中です。' : '该项目的注意事项正在准备中。'}
      </div>
    );
  }

  // ここまで来れば doc はある（過去の成功キャッシュも含む）
  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4">
      {doc?.title && <h4 className="font-semibold text-slate-800 mb-2">{doc.title}</h4>}
      {doc?.html && (
        <div
          className="prose prose-slate max-w-none text-[15px] leading-relaxed"
          dangerouslySetInnerHTML={{ __html: doc.html }}
        />
      )}
    </div>
  );
};

export default NoticeContent;
